import { FC, PropsWithChildren, createContext, useEffect, useMemo } from 'react';

import capitalize from 'lodash/capitalize';

type UserType = 'backoffice' | 'location' | 'advertiser' | 'service' | undefined;

export const UserTypeContext = createContext<UserType>(undefined);

export const UserTypeContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const userType = useMemo(() => getUserTypeFromHostname(window.location.hostname), []);

  useEffect(() => {
    document.title = `Generatik - ${capitalize(userType)}`;

    const color = userType
      ? {
          backoffice: '#FFB147',
          location: '#8E6DE1',
          advertiser: '#DE56B7',
          service: '#4592FA',
        }[userType] || '#000000'
      : '#000000';

    fetch('/logo.svg')
      .then((response) => response.text())
      .then((svgText) => {
        const coloredSvg = svgText
          .replace(/fill="[^"]*"/g, `fill="${color}"`)
          .replace(/stroke="[^"]*"/g, `stroke="${color}"`);

        const favicon =
          document.querySelector<HTMLLinkElement>("link[rel='icon']") ||
          document.createElement('link');
        favicon.rel = 'icon';
        favicon.type = 'image/svg+xml';
        favicon.href = `data:image/svg+xml,${encodeURIComponent(coloredSvg)}`;

        if (!document.querySelector("link[rel='icon']")) {
          document.head.appendChild(favicon);
        }
      })
      .catch(() => {
        // Keep the default favicon.
      });
  }, [userType]);

  return <UserTypeContext.Provider value={userType}>{children}</UserTypeContext.Provider>;
};

// For code that renders outside the provider (the root error boundary).
export function getUserTypeFromHostname(hostname: string): UserType {
  if (hostname.includes('backoffice')) return 'backoffice';
  if (hostname.includes('location')) return 'location';
  if (hostname.includes('advertiser')) return 'advertiser';
  if (hostname.includes('services')) return 'service';
  return undefined;
}
